import { type UNIQUE_SIDECAR_BATCH } from "../backend/capabilities/bundle-registry";
import { type BundleVerdictOf } from "../backend/capabilities/resolve";
import {
  type BackendIdentity,
  type GraphBackend,
  type GraphEntityReadBackend,
  type QueryExecutionBackend,
  type RawQueryExecutionBackend,
  type SchemaReadBackend,
  type SqlCompilationBackend,
  type TransactionBackend,
} from "../backend/types";
import {
  type AllNodeTypes,
  type EdgeKinds,
  type GraphDef,
  type NodeKinds,
} from "../core/define-graph";
import { type ReadCoordinate } from "../core/temporal";
import {
  type AnyEdgeType,
  type EdgeId,
  type NodeId,
  type NodeType,
} from "../core/types";
import { ConfigurationError } from "../errors";
import { type IdentityReadFacade } from "../identity/types";
import { type InitialQueryBuilder } from "../query/builder";
import type { EvolutionPlan } from "../schema/evolution-plan";
import { typeGraphGlobalSymbol } from "../utils/global-symbol";
import { requireDefined } from "../utils/presence";
import { type InternalGraphAlgorithms } from "./algorithms";
import type { Store } from "./store";
import {
  type InternalSubgraphOptions,
  type SubgraphProject,
  type SubgraphResult,
} from "./subgraph";
import {
  type Edge,
  type Node,
  type RecordedScanOptions,
  type RecordedScanPage,
  TRANSACTION_RUNTIME,
} from "./types";

export const STORE_RUNTIME: unique symbol =
  typeGraphGlobalSymbol("store-runtime-v1");

/**
 * @internal Operations used by Store-owned views. The port is absent from the
 * public Store contract and non-enumerable at runtime. JavaScript reflection
 * can still discover symbol properties, so this is an unsupported internal
 * surface rather than a security boundary.
 */
export type StoreRuntime<G extends GraphDef> = Readonly<{
  backend: GraphBackend;
  /** Constructs a plan-owned resulting-schema view for outside-transaction merge planning. */
  evolutionPlanningTarget?: (plan: EvolutionPlan) => Store<G>;
  /**
   * @internal Whether TypeGraph itself performs recorded-time capture for
   * this store — recorded relations, a TypeGraph clock, the write-fence/
   * schema-lock machinery capture needs. This is `Store`'s private
   * `#captureEnabled`, distinct from the public `historyEnabled` getter
   * (which answers "was `history: true` requested," true under
   * engine-native ownership too, where the engine tracks history on its
   * own and none of the TypeGraph-relations machinery below runs). A reader
   * of a recorded relation's own columns — `recordedRelationsLineage`, the
   * trusted-import bypass refusal, a revision-anchor lineage delta, the
   * capture-only merge-transaction isolation choice — consults this member,
   * never the public getter, so it cannot be fooled by an engine-native
   * store into reading a recorded relation the engine never populates.
   *
   * Optional at this boundary for the same contravariant-reach reason as
   * `uniqueSidecarBatch` below: the one real producer (`store.ts`'s
   * constructor) always populates it, and a consumer asserts it with
   * {@link storeCaptureEnabled}.
   */
  captureEnabled?: boolean;
  /**
   * @internal The `uniqueSidecarBatch` bundle's verdict, resolved once at
   * store construction against `backend` (ruling B8 spec item 2) and exposed
   * here so a Store-owned view (provenance's fact close/reopen) can build a
   * {@link file://./claims/node-claims.ts NodeClaimContext} without re-minting
   * a second verdict for the same backend — the same reason `backend` itself
   * is exposed here rather than reconstructed.
   *
   * Optional at this boundary — a `StoreRuntime`-shaped value is a
   * contravariant (externally-authorable) position, so a new REQUIRED member
   * here would be a breaking change (`scripts/api-surface-compat.ts`).
   * Required after resolution instead, the same pattern
   * `CompileQueryOptions.recursiveTraversal` uses: the one real producer
   * (`store.ts`'s constructor) always populates it, and the one real
   * consumer (`provenance/index.ts`) asserts it with `requireDefined`.
   */
  uniqueSidecarBatch?: BundleVerdictOf<typeof UNIQUE_SIDECAR_BATCH> | undefined;
  /**
   * @internal The backend this Store's queries actually execute through for
   * `target` — the Store's own backend when `target` is omitted.
   *
   * It is the SAME private construction the query path uses rather than a
   * reconstruction of it, because the object it returns is the one a lost
   * derivation corrupts: the hooked query backend is a transient local inside
   * query construction that is stored nowhere, so no other handle on it exists
   * and a regression there is invisible to every assertion about
   * {@link StoreRuntime.backend}.
   *
   * NOTE for anything asserting on it: with no query hook configured this
   * returns its argument unchanged, so a hookless Store answers with the very
   * object it was handed and a comparison against that object is a tautology.
   */
  queryBackend: (target?: GraphBackend | TransactionBackend) => GraphBackend;
  sealedQuery: (coordinate: ReadCoordinate) => InitialQueryBuilder<G, "sealed">;
  recordedNodeGetById: <N extends NodeType>(
    kind: string,
    id: NodeId<N>,
    coordinate: ReadCoordinate,
  ) => Promise<Node<N> | undefined>;
  recordedNodeGetByIds: <N extends NodeType>(
    kind: string,
    ids: readonly NodeId<N>[],
    coordinate: ReadCoordinate,
  ) => Promise<readonly (Node<N> | undefined)[]>;
  recordedNodeScan: <N extends NodeType>(
    kind: string,
    coordinate: ReadCoordinate,
    options?: RecordedScanOptions,
  ) => Promise<RecordedScanPage<Node<N>>>;
  recordedEdgeGetById: <E extends AnyEdgeType>(
    kind: string,
    id: EdgeId<E>,
    coordinate: ReadCoordinate,
  ) => Promise<Edge<E> | undefined>;
  recordedEdgeGetByIds: <E extends AnyEdgeType>(
    kind: string,
    ids: readonly EdgeId<E>[],
    coordinate: ReadCoordinate,
  ) => Promise<readonly (Edge<E> | undefined)[]>;
  recordedEdgeScan: <E extends AnyEdgeType>(
    kind: string,
    coordinate: ReadCoordinate,
    options?: RecordedScanOptions,
  ) => Promise<RecordedScanPage<Edge<E>>>;
  subgraphAtCoordinate: <
    const EK extends EdgeKinds<G>,
    const NK extends NodeKinds<G> = NodeKinds<G>,
    const P extends SubgraphProject<G, NK, EK> | undefined = undefined,
  >(
    rootId: NodeId<AllNodeTypes<G>>,
    options: InternalSubgraphOptions<G, EK, NK, P>,
  ) => Promise<SubgraphResult<G, NK, EK, P>>;
  algorithmsAtCoordinate: (
    coordinate: ReadCoordinate,
  ) => InternalGraphAlgorithms<G>;
  identityAtCoordinate: (coordinate: ReadCoordinate) => IdentityReadFacade<G>;
  rebuildIdentityClosure: () => Promise<void>;
  validateIdentity: () => Promise<void>;
  /**
   * Validates one final resolved node write set, then clears the affected
   * nodes' claim rows so its upserts may take their approved keys in any order,
   * and re-takes the complete claim set once the writes have landed. The caller
   * must supply a transaction-bound backend.
   *
   * The clear is by OWNER, so it takes every claim the affected nodes hold —
   * uniqueness and `disjointWith` alike — and the rebuild therefore goes through
   * the same claim writer an ordinary create uses rather than a uniqueness-only
   * insert. See `store/claims/resolved-node-claims.ts`.
   */
  applyResolvedNodeUniqueness: <Output>(
    target: TransactionBackend,
    writes: Readonly<{
      upserts: readonly Readonly<{
        kind: string;
        id: string;
        props: Readonly<Record<string, unknown>>;
      }>[];
      releases: readonly Readonly<{ kind: string; id: string }>[];
    }>,
    apply: () => Promise<Output>,
  ) => Promise<Output>;
  /**
   * @internal Reads the graph's identity assertions in transfer shape, honoring
   * this store's SQL binding. Used by interchange export, base-version
   * fingerprinting, and merge staging/diff.
   */
  readCurrentIdentityAssertions: (
    mode: "state" | "archival",
    options?: Readonly<{
      nodeKinds?: readonly string[];
      includeDeleted?: boolean;
    }>,
  ) => Promise<
    readonly Readonly<{
      id: string;
      relation: "same" | "different";
      a: Readonly<{ kind: string; id: string }>;
      b: Readonly<{ kind: string; id: string }>;
      validFrom: string;
      validTo?: string | undefined;
      endedBy?: Readonly<{ kind: string; id: string }> | undefined;
    }>[]
  >;
  /**
   * Live nodes (registry kinds only) sharing any of the given bare ids —
   * the cross-kind peer set same-id folding would join. Used by graph-merge's
   * plan-time contradiction simulation to seed its node universe.
   */
  liveNodesSharingIds: (
    ids: readonly string[],
    target?: GraphBackend | TransactionBackend,
  ) => Promise<readonly Readonly<{ kind: string; id: string }>[]>;
  /**
   * Every stored assertion row (ended rows included) for the given assertion
   * ids — the rows the import coordinator's id-conflict check compares
   * against. Used by graph-merge to validate the one-id-one-truth invariant
   * at plan time and inside the commit transaction.
   */
  identityAssertionRowsByIds: (
    ids: readonly string[],
    target?: GraphBackend | TransactionBackend,
  ) => Promise<
    ReadonlyMap<
      string,
      Readonly<{
        id: string;
        relation: "same" | "different";
        a: Readonly<{ kind: string; id: string }>;
        b: Readonly<{ kind: string; id: string }>;
        validFrom: string;
        validTo?: string | undefined;
        endedBy?: Readonly<{ kind: string; id: string }> | undefined;
      }>
    >
  >;
  /**
   * The CURRENT structural identity class (materialized closure: folds plus
   * asserted links) of each reference, keyed by `refKey` — the
   * `JSON.stringify([kind, id])` serialization exported from
   * `identity/service`, which callers must use to probe the returned map. A
   * missing node coalesces to its singleton. Used by graph-merge's fold-peer
   * window guard to detect class-transitive drift in the plan→commit window.
   */
  structuralIdentityClasses: (
    references: readonly Readonly<{ kind: string; id: string }>[],
    target?: GraphBackend | TransactionBackend,
  ) => Promise<
    ReadonlyMap<string, readonly Readonly<{ kind: string; id: string }>[]>
  >;
  identityAssertionsAtTarget: (
    target: GraphBackend | TransactionBackend,
    mode?: "state" | "archival",
  ) => Promise<
    readonly Readonly<{
      id: string;
      relation: "same" | "different";
      a: Readonly<{ kind: string; id: string }>;
      b: Readonly<{ kind: string; id: string }>;
      validFrom: string;
      validTo?: string | undefined;
      endedBy?: Readonly<{ kind: string; id: string }> | undefined;
    }>[]
  >;
  readIdentityAssertionPageAtTarget: (
    target: GraphBackend | TransactionBackend,
    mode: "state" | "archival",
    options: Readonly<{
      nodeKinds?: readonly string[];
      includeDeleted?: boolean;
      after?: string;
      limit: number;
    }>,
  ) => Promise<
    Readonly<{
      assertions: readonly Readonly<{
        id: string;
        relation: "same" | "different";
        a: Readonly<{ kind: string; id: string }>;
        b: Readonly<{ kind: string; id: string }>;
        validFrom: string;
        validTo?: string | undefined;
        endedBy?: Readonly<{ kind: string; id: string }> | undefined;
      }>[];
      nextAfter?: string;
      done: boolean;
    }>
  >;
  lockIdentityImportTarget: (
    target: Readonly<
      BackendIdentity &
        GraphEntityReadBackend &
        SchemaReadBackend &
        QueryExecutionBackend &
        SqlCompilationBackend &
        RawQueryExecutionBackend &
        Pick<GraphBackend, "executeStatement">
    >,
  ) => Promise<void>;
  foldImportedIdentityNodes: (
    target: Readonly<
      BackendIdentity &
        GraphEntityReadBackend &
        SchemaReadBackend &
        QueryExecutionBackend &
        SqlCompilationBackend &
        RawQueryExecutionBackend &
        Pick<GraphBackend, "executeStatement">
    >,
    references: readonly Readonly<{ kind: string; id: string }>[],
  ) => Promise<void>;
  importIdentityAssertionsAtTarget: (
    target: Readonly<
      BackendIdentity &
        GraphEntityReadBackend &
        SchemaReadBackend &
        QueryExecutionBackend &
        SqlCompilationBackend &
        RawQueryExecutionBackend &
        Pick<GraphBackend, "executeStatement">
    >,
    assertions: readonly Readonly<{
      id: string;
      relation: "same" | "different";
      a: Readonly<{ kind: string; id: string }>;
      b: Readonly<{ kind: string; id: string }>;
      validFrom: string;
      validTo?: string | undefined;
      endedBy?: Readonly<{ kind: string; id: string }> | undefined;
    }>[],
    mode: "state" | "archival",
  ) => Promise<Readonly<{ created: number; skipped: number }>>;
  applyIdentityMergeAtTarget: (
    target: GraphBackend | TransactionBackend,
    retractions: readonly Readonly<{
      id: string;
      relation: "same" | "different";
      a: Readonly<{ kind: string; id: string }>;
      b: Readonly<{ kind: string; id: string }>;
      validFrom: string;
      validTo?: string | undefined;
      endedBy?: Readonly<{ kind: string; id: string }> | undefined;
    }>[],
    assertions: readonly Readonly<{
      id: string;
      relation: "same" | "different";
      a: Readonly<{ kind: string; id: string }>;
      b: Readonly<{ kind: string; id: string }>;
      validFrom: string;
      validTo?: string | undefined;
      endedBy?: Readonly<{ kind: string; id: string }> | undefined;
    }>[],
  ) => Promise<Readonly<{ created: number; retracted: number }>>;
  /**
   * Proves the identity classes of `seeds` carry no contradiction in the state
   * the caller's transaction has just written — the post-write half of
   * graph-merge's identity correctness, scoped to the classes the merge
   * touched. A refusal aborts the caller's transaction; identity-disabled
   * graphs resolve immediately.
   */
  assertIdentityClassesConsistentAtTarget: (
    target: GraphBackend | TransactionBackend,
    seeds: readonly Readonly<{ kind: string; id: string }>[],
  ) => Promise<void>;
}>;

export function storeRuntime<G extends GraphDef>(
  store: Readonly<{ [STORE_RUNTIME]?: StoreRuntime<G> }>,
): StoreRuntime<G> {
  const runtime = store[STORE_RUNTIME];
  if (runtime === undefined) {
    throw new TypeError(
      "Cannot access this Store's runtime port. The Store may come from an incompatible TypeGraph version.",
    );
  }
  return runtime;
}

export function storeBackend<G extends GraphDef>(
  store: Readonly<{ [STORE_RUNTIME]?: StoreRuntime<G> }>,
): GraphBackend {
  return storeRuntime(store).backend;
}

/**
 * Whether TypeGraph itself performs recorded-time capture for `store` — see
 * {@link StoreRuntime.captureEnabled}. Consult this, never the public
 * `historyEnabled` getter, when the decision at hand is specifically about
 * TypeGraph's own recorded relations (a reader of their columns, the
 * capture-only merge-transaction isolation choice, the trusted-import
 * bypass refusal): `historyEnabled` answers "was `history: true`
 * requested," true for an engine-native store too, which never populates
 * those relations.
 */
export function storeCaptureEnabled<G extends GraphDef>(
  store: Readonly<{ [STORE_RUNTIME]?: StoreRuntime<G> }>,
): boolean {
  return requireDefined(
    storeRuntime(store).captureEnabled,
    "Cannot read this Store's capture flag. The Store may come from an incompatible TypeGraph version.",
  );
}

/**
 * The backend a Store's queries execute through — see
 * {@link StoreRuntime.queryBackend}, including its note about hookless Stores.
 */
export function storeQueryBackend<G extends GraphDef>(
  store: Readonly<{ [STORE_RUNTIME]?: StoreRuntime<G> }>,
  target?: GraphBackend | TransactionBackend,
): GraphBackend {
  return storeRuntime(store).queryBackend(target);
}

type TransactionRuntimePort = Readonly<{
  [TRANSACTION_RUNTIME]?: Readonly<{
    backend: TransactionBackend;
    runNodeOperationHooks: TransactionNodeOperationHookRunner;
  }>;
}>;

type TransactionNodeOperationHookRunner = <T>(
  operation: "create" | "update" | "delete",
  kind: string,
  id: string,
  fn: () => Promise<T>,
) => Promise<T>;

const transactionStoreOwners = new WeakMap<object, object>();
const evolvedTransactionOriginalOwners = new WeakMap<object, object>();
const evolvedTransactionStores = new WeakMap<object, object>();
const activeTransactionContexts = new WeakSet<object>();

/** Binds the hidden context port to the Store that created its collections. */
export function bindTransactionStore<G extends GraphDef>(
  transaction: TransactionRuntimePort,
  store: Readonly<{ [STORE_RUNTIME]?: StoreRuntime<G> }>,
): void {
  const runtime = requireDefined(transaction[TRANSACTION_RUNTIME]);
  transactionStoreOwners.set(runtime, storeRuntime(store));
}

/** Binds an evolved callback to its original Store and temporary evolved view. */
export function bindEvolvedTransactionStore<TStore extends object>(
  transaction: TransactionRuntimePort,
  originalStore: TStore,
  evolvedStore: TStore,
): void {
  const runtime = requireDefined(transaction[TRANSACTION_RUNTIME]);
  transactionStoreOwners.set(runtime, storeRuntime(evolvedStore));
  evolvedTransactionOriginalOwners.set(runtime, storeRuntime(originalStore));
  evolvedTransactionStores.set(runtime, evolvedStore);
}

/** Resolves the Store whose graph actually owns an active callback context. */
export function resolveEvolvedTransactionStore<TStore extends object>(
  transaction: TransactionRuntimePort,
  originalStore: TStore,
): TStore {
  const runtime = transaction[TRANSACTION_RUNTIME];
  if (runtime === undefined || !activeTransactionContexts.has(runtime)) {
    throw new ConfigurationError(
      "The merge transaction must belong to an active callback of the target Store.",
      { capability: "mergeTransactionStore" },
    );
  }
  const originalRuntime = storeRuntime(originalStore);
  if (transactionStoreOwners.get(runtime) === originalRuntime) {
    return originalStore;
  }
  if (evolvedTransactionOriginalOwners.get(runtime) === originalRuntime) {
    const evolvedStore = evolvedTransactionStores.get(runtime);
    if (evolvedStore !== undefined) return evolvedStore as TStore;
  }
  throw new ConfigurationError(
    "The merge transaction must belong to an active callback of the target Store.",
    { capability: "mergeTransactionStore" },
  );
}

/** The callback boundary owns the lifetime of every measurable context overlay. */
export async function runInTransactionContext<
  T,
  Context extends TransactionRuntimePort,
>(context: Context, fn: (context: Context) => Promise<T>): Promise<T> {
  const runtime = requireDefined(context[TRANSACTION_RUNTIME]);
  activeTransactionContexts.add(runtime);
  try {
    return await fn(context);
  } finally {
    activeTransactionContexts.delete(runtime);
  }
}

/** Returns the full backend for privileged transaction-bound internals. */
export function transactionBackend(
  transaction: TransactionRuntimePort,
): TransactionBackend {
  const runtime = transaction[TRANSACTION_RUNTIME];
  if (runtime === undefined) {
    throw new TypeError(
      "Cannot access this transaction's runtime port. The transaction may come from an incompatible TypeGraph version.",
    );
  }
  return runtime.backend;
}

/** Returns the hook runner paired with a transaction's internal backend. */
export function transactionNodeOperationHookRunner(
  transaction: TransactionRuntimePort,
): TransactionNodeOperationHookRunner {
  const runtime = transaction[TRANSACTION_RUNTIME];
  if (runtime === undefined) {
    throw new TypeError(
      "Cannot access this transaction's runtime port. The transaction may come from an incompatible TypeGraph version.",
    );
  }
  return runtime.runNodeOperationHooks;
}
